import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import type { KeyboardEvent, ReactNode } from "react";

import type { DashboardCardId } from "_shared/_views/dashboards/dashboard-card-order";

/**
 * One cell of the dashboard grid, which the reader can pick up and put somewhere else.
 *
 * The slot wraps a card rather than the cards knowing about dragging, so a new card joins the
 * arrangement by being listed in `dashboardCardIds` and nothing else. It is both the draggable and
 * the droppable: a card is dropped *onto* another card and takes its place, which is the whole
 * interaction and needs no separate gaps to aim at.
 *
 * Three cards render `null` when the reader lacks the permission for them, and a wrapper around
 * nothing would still take a column and leave a hole in the grid. `:has` is what collapses the slot
 * in that case — the slot cannot ask its child whether it drew anything, but CSS can.
 */
const Slot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dragging" && prop !== "over",
})<{ dragging: boolean; over: boolean }>(({ dragging, over, theme }) => ({
  position: "relative",
  display: "flex",
  // The card fills the slot, so a row of slots still stretches to a common height.
  "& > .MuiCard-root": {
    flex: 1,
    minWidth: 0,
  },
  "&:not(:has(> .MuiCard-root))": {
    display: "none",
  },
  borderRadius: theme.spacing(2),
  outline: over && !dragging ? `2px dashed ${theme.palette.primary.main}` : "none",
  outlineOffset: 4,
  opacity: dragging ? 0.65 : 1,
  zIndex: dragging ? 2 : undefined,
  // A card being carried should not animate back to where it was on every pointer move.
  transition: dragging ? "none" : theme.transitions.create(["outline-color", "opacity"]),
  "&:hover .DashboardCardHandle, &:focus-within .DashboardCardHandle": {
    opacity: 1,
  },
}));

/**
 * Out of the way until it is wanted, and in a corner of nothing.
 *
 * It straddles the middle of the card's top edge, sitting in the grid's own gap rather than inside
 * the card. The top left is the eyebrow and the title under it is the card's link (#6584), and a
 * handle over either would cover the one or push the other sideways when it appears. Reserving space
 * in the header instead is worse again: it costs every card a column of padding it does not otherwise
 * need. The drag starts from this handle alone, so pressing the title follows the link.
 *
 * Only its opacity changes, never its position, so nothing under it moves as it comes and goes. It
 * is always reachable by keyboard, where focus brings it back.
 */
const Handle = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  opacity: 0,
  cursor: "grab",
  // Without this a touch drag scrolls the page instead of picking the card up.
  touchAction: "none",
  transition: theme.transitions.create(["opacity"]),
  "&:focus-visible": {
    opacity: 1,
  },
  "&:active": {
    cursor: "grabbing",
  },
}));

export type DraggableDashboardCardProps = {
  cardId: DashboardCardId;
  /** What the card is called, so the handle can say which card it moves. */
  label: string;
  /** Where this card sits now, and how many there are, both 1-based for the reader. */
  position: number;
  total: number;
  onMoveTo: (cardId: DashboardCardId, toIndex: number) => void;
  children: ReactNode;
};

export default function DraggableDashboardCard(props: Readonly<DraggableDashboardCardProps>) {
  const draggable = useDraggable({ id: props.cardId });
  const droppable = useDroppable({ id: props.cardId });

  const position = props.position;
  const cardId = props.cardId;
  const onMoveTo = props.onMoveTo;

  /**
   * The keyboard's own way to rearrange, rather than a keyboard imitation of dragging.
   *
   * dnd-kit's `KeyboardSensor` moves a pointer around and leaves the reader to find a drop target
   * by feel, which on a grid that reflows its columns is guesswork. Moving the card one place per
   * key press says exactly what will happen and needs no drop target at all.
   */
  const moveByKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      onMoveTo(cardId, position - 2);
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      onMoveTo(cardId, position);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      onMoveTo(cardId, 0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      onMoveTo(cardId, props.total - 1);
    }
  };

  const setSlotRef = (node: HTMLElement | null) => {
    draggable.setNodeRef(node);
    droppable.setNodeRef(node);
  };

  return (
    <Slot
      ref={setSlotRef}
      dragging={draggable.isDragging}
      over={droppable.isOver}
      style={{ transform: CSS.Translate.toString(draggable.transform) }}
    >
      <Handle
        {...draggable.listeners}
        // After the listeners, deliberately: this replaces dnd-kit's own `onKeyDown`, so the arrow
        // keys move the card outright rather than starting a keyboard drag the reader then has to
        // aim and drop.
        onKeyDown={moveByKey}
        className="DashboardCardHandle"
        size="small"
        aria-label={`Move ${props.label}. Card ${position} of ${props.total}. Use the arrow keys.`}
      >
        <DragIndicatorRoundedIcon fontSize="small" />
      </Handle>
      {props.children}
    </Slot>
  );
}
